/**
 * "This user's Get Started checklist just changed", for the copies of it that are already on screen.
 *
 * Clearing the cache in `onboarding.ts` is enough for the next component that mounts, but not for
 * one that is mounted right now. Restoring the checklist from the sidebar dialog while `/players` is
 * behind it left the panel there holding its old "dismissed" answer until the coach navigated away
 * and back, which reads as a button that did nothing.
 *
 * Its own module, rather than an export of `onboarding.ts`, because the component tests mock that
 * service wholesale with only the calls they exercise. A listener living there would be missing from
 * every one of those mocks, and every one of them would fail to import.
 *
 * Fired on dismiss and restore only. Sign-out and an organization switch also clear the cache, but
 * they tear down or re-key the tree that holds these readers, and a refetch fired mid-sign-out would
 * go out without a token.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

/** Calls `listener` whenever the checklist changes. Returns the unsubscribe. */
export const subscribeToGetStartedChanges = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Tells every mounted reader to read the checklist again. */
export const notifyGetStartedChanged = (): void => {
  // Copied first, so a listener that unsubscribes while being called does not skip its neighbour.
  [...listeners].forEach((listener) => listener());
};
